import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Repeat,
  Check,
  ChevronDown,
  ExternalLink,
  Calendar,
} from "lucide-react";
// office Hub css and 8127 to 8783
const RecurringTasksEnhanced = () => {
  const [recurringEnabled, setRecurringEnabled] = useState(true);
  const [frequency, setFrequency] = useState("Monthly");

  // Monthly states
  const [monthlyCreationDay, setMonthlyCreationDay] = useState("2th");
  const [monthlyCreationPeriod, setMonthlyCreationPeriod] =
    useState("Of Every Month");
  const [monthlyDueDay, setMonthlyDueDay] = useState("11th");
  const [monthlyDuePeriod, setMonthlyDuePeriod] = useState("Of Every Month");

  // Yearly states
  const [yearlyCreationDay, setYearlyCreationDay] = useState("2 Dec");
  const [yearlyCreationPeriod, setYearlyCreationPeriod] = useState(
    "Of Every Calendar Year",
  );
  const [yearlyDueDay, setYearlyDueDay] = useState("1 Jan");
  const [yearlyDuePeriod, setYearlyDuePeriod] = useState(
    "Of Every Calendar Year",
  );

  // Half-yearly states
  const [halfYearlyCreationDay, setHalfYearlyCreationDay] = useState("2nd");
  const [halfYearlyCreationMonth1, setHalfYearlyCreationMonth1] =
    useState("April");
  const [halfYearlyCreationMonth2, setHalfYearlyCreationMonth2] =
    useState("Oct");
  const [halfYearlyDueDay, setHalfYearlyDueDay] = useState("11th");
  const [halfYearlyDueMonth1, setHalfYearlyDueMonth1] = useState("May");
  const [halfYearlyDueMonth2, setHalfYearlyDueMonth2] = useState("Nov");

  // Quarterly states
  const [quarterlyCreationDay, setQuarterlyCreationDay] = useState("2th");
  const [quarterlyCreationPosition, setQuarterlyCreationPosition] =
    useState("Second");
  const [quarterlyCreationType, setQuarterlyCreationType] = useState(
    "Month of every quarter",
  );
  const [quarterlyDueDay, setQuarterlyDueDay] = useState("11th");
  const [quarterlyDuePosition, setQuarterlyDuePosition] = useState("Second");
  const [quarterlyDueType, setQuarterlyDueType] = useState(
    "Month of every quarter",
  );

  const [periodType, setPeriodType] = useState("recently-completed");
  const [autoAssignEnabled, setAutoAssignEnabled] = useState(false);
  const [autoAssignTo, setAutoAssignTo] = useState("Select");
  const [offset, setOffset] = useState(13); // default small screen

  useEffect(() => {
    const handleResize = () => {
      // Define "big screen" breakpoint (e.g., 768px or 1024px)
      const isBigScreen = window.innerWidth >= 768;
      setOffset(isBigScreen ? 28 : 13);
    };

    handleResize(); // set initial value
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // Example data for Monthly
  const monthlyRecentlyCompleted = [
    {
      task: "FY 2025-26 | January",
      creationDate: "02-Feb-2026",
      dueDate: "11-Feb-2026",
    },
    {
      task: "FY 2025-26 | February",
      creationDate: "02-Mar-2026",
      dueDate: "11-Mar-2026",
    },
    {
      task: "FY 2025-26 | March",
      creationDate: "02-Apr-2026",
      dueDate: "11-Apr-2026",
    },
    {
      task: "FY 2026-27 | April",
      creationDate: "02-May-2026",
      dueDate: "11-May-2026",
    },
  ];

  const monthlyCurrentlyRunning = [
    {
      task: "FY 2025-26 | January",
      creationDate: "02-Jan-2026",
      dueDate: "11-Jan-2026",
    },
    {
      task: "FY 2025-26 | February",
      creationDate: "02-Feb-2026",
      dueDate: "11-Feb-2026",
    },
    {
      task: "FY 2025-26 | March",
      creationDate: "02-Mar-2026",
      dueDate: "11-Mar-2026",
    },
    {
      task: "FY 2026-27 | April",
      creationDate: "02-Apr-2026",
      dueDate: "11-Apr-2026",
    },
  ];

  // Example data for Yearly
  const yearlyRecentlyCompleted = [
    {
      task: "FY 2024-25 | Annual",
      creationDate: "02-Dec-2025",
      dueDate: "01-Jan-2026",
    },
    {
      task: "FY 2025-26 | Annual",
      creationDate: "02-Dec-2026",
      dueDate: "01-Jan-2027",
    },
    {
      task: "FY 2026-27 | Annual",
      creationDate: "02-Dec-2027",
      dueDate: "01-Jan-2028",
    },
  ];

  const yearlyCurrentlyRunning = [
    {
      task: "FY 2025-26 | Annual",
      creationDate: "02-Dec-2025",
      dueDate: "01-Jan-2026",
    },
    {
      task: "FY 2026-27 | Annual",
      creationDate: "02-Dec-2026",
      dueDate: "01-Jan-2027",
    },
    {
      task: "FY 2027-28 | Annual",
      creationDate: "02-Dec-2027",
      dueDate: "01-Jan-2028",
    },
  ];

  // Example data for Half-yearly
  const halfYearlyRecentlyCompleted = [
    {
      task: "FY 2025-26 | Oct-Mar",
      creationDate: "02-Apr-2026",
      dueDate: "11-May-2026",
    },
    {
      task: "FY 2026-27 | Apr-Sep",
      creationDate: "02-Oct-2026",
      dueDate: "11-Nov-2026",
    },
    {
      task: "FY 2026-27 | Oct-Mar",
      creationDate: "02-Apr-2027",
      dueDate: "11-May-2027",
    },
  ];

  const halfYearlyCurrentlyRunning = [
    {
      task: "FY 2025-26 | Apr-Sep",
      creationDate: "02-Apr-2026",
      dueDate: "11-May-2026",
    },
    {
      task: "FY 2025-26 | Oct-Mar",
      creationDate: "02-Oct-2026",
      dueDate: "11-Nov-2026",
    },
    {
      task: "FY 2026-27 | Apr-Sep",
      creationDate: "02-Apr-2027",
      dueDate: "11-May-2027",
    },
  ];

  // Example data for Quarterly
  const quarterlyRecentlyCompleted = [
    {
      task: "FY 2025-26 | Oct-Dec",
      creationDate: "02-Jan-2026",
      dueDate: "11-Feb-2026",
    },
    {
      task: "FY 2025-26 | Jan-Mar",
      creationDate: "02-Apr-2026",
      dueDate: "11-May-2026",
    },
    {
      task: "FY 2026-27 | Apr-Jun",
      creationDate: "02-Jul-2026",
      dueDate: "11-Aug-2026",
    },
    {
      task: "FY 2026-27 | Jul-Sep",
      creationDate: "02-Oct-2026",
      dueDate: "11-Nov-2026",
    },
  ];

  const quarterlyCurrentlyRunning = [
    {
      task: "FY 2025-26 | Jan-Mar",
      creationDate: "02-Jan-2026",
      dueDate: "11-Feb-2026",
    },
    {
      task: "FY 2026-27 | Apr-Jun",
      creationDate: "02-Apr-2026",
      dueDate: "11-May-2026",
    },
    {
      task: "FY 2026-27 | Jul-Sep",
      creationDate: "02-Jul-2026",
      dueDate: "11-Aug-2026",
    },
    {
      task: "FY 2027-28 | Oct-Dec",
      creationDate: "02-Oct-2026",
      dueDate: "11-Nov-2026",
    },
  ];

  const getExampleData = () => {
    if (frequency === "Monthly") {
      return periodType === "recently-completed"
        ? monthlyRecentlyCompleted
        : monthlyCurrentlyRunning;
    } else if (frequency === "Yearly") {
      return periodType === "recently-completed"
        ? yearlyRecentlyCompleted
        : yearlyCurrentlyRunning;
    } else if (frequency === "Half-yearly") {
      return periodType === "recently-completed"
        ? halfYearlyRecentlyCompleted
        : halfYearlyCurrentlyRunning;
    } else if (frequency === "Quarterly") {
      return periodType === "recently-completed"
        ? quarterlyRecentlyCompleted
        : quarterlyCurrentlyRunning;
    }
    return [];
  };

  const getExampleText = () => {
    if (frequency === "Monthly") {
      if (periodType === "recently-completed") {
        return (
          <>
            On{" "}
            <span className="recurring-tasks__example-highlight">
              02-Mar-2026
            </span>
            , a task is created for{" "}
            <span className="recurring-tasks__example-highlight">
              FY 2025-26 | February
            </span>
            <br />
            On{" "}
            <span className="recurring-tasks__example-highlight">
              02-Apr-2026
            </span>
            , a task is created for{" "}
            <span className="recurring-tasks__example-highlight">
              FY 2025-26 | March
            </span>
            , and so on.
          </>
        );
      } else {
        return (
          <>
            On{" "}
            <span className="recurring-tasks__example-highlight">
              02-Mar-2026
            </span>
            , a task is created for{" "}
            <span className="recurring-tasks__example-highlight">
              FY 2025-26 | March
            </span>
            <br />
            On{" "}
            <span className="recurring-tasks__example-highlight">
              02-Apr-2026
            </span>
            , a task is created for{" "}
            <span className="recurring-tasks__example-highlight">
              FY 2026-27 | April
            </span>
            , and so on.
          </>
        );
      }
    } else if (frequency === "Yearly") {
      if (periodType === "recently-completed") {
        return (
          <>
            On{" "}
            <span className="recurring-tasks__example-highlight">
              02-Dec-2026
            </span>
            , a task is created for{" "}
            <span className="recurring-tasks__example-highlight">
              FY 2025-26 | Annual
            </span>
            <br />
            On{" "}
            <span className="recurring-tasks__example-highlight">
              02-Dec-2027
            </span>
            , a task is created for{" "}
            <span className="recurring-tasks__example-highlight">
              FY 2026-27 | Annual
            </span>
            , and so on.
          </>
        );
      } else {
        return (
          <>
            On{" "}
            <span className="recurring-tasks__example-highlight">
              02-Dec-2026
            </span>
            , a task is created for{" "}
            <span className="recurring-tasks__example-highlight">
              FY 2026-27 | Annual
            </span>
            <br />
            On{" "}
            <span className="recurring-tasks__example-highlight">
              02-Dec-2027
            </span>
            , a task is created for{" "}
            <span className="recurring-tasks__example-highlight">
              FY 2027-28 | Annual
            </span>
            , and so on.
          </>
        );
      }
    } else if (frequency === "Half-yearly") {
      if (periodType === "recently-completed") {
        return (
          <>
            On{" "}
            <span className="recurring-tasks__example-highlight">
              02-Apr-2026
            </span>
            , a task is created for{" "}
            <span className="recurring-tasks__example-highlight">
              FY 2025-26 | Oct-Mar
            </span>
            <br />
            On{" "}
            <span className="recurring-tasks__example-highlight">
              02-Oct-2026
            </span>
            , a task is created for{" "}
            <span className="recurring-tasks__example-highlight">
              FY 2026-27 | Apr-Sep
            </span>
            , and so on.
          </>
        );
      } else {
        return (
          <>
            On{" "}
            <span className="recurring-tasks__example-highlight">
              02-Apr-2026
            </span>
            , a task is created for{" "}
            <span className="recurring-tasks__example-highlight">
              FY 2025-26 | Apr-Sep
            </span>
            <br />
            On{" "}
            <span className="recurring-tasks__example-highlight">
              02-Oct-2026
            </span>
            , a task is created for{" "}
            <span className="recurring-tasks__example-highlight">
              FY 2025-26 | Oct-Mar
            </span>
            , and so on.
          </>
        );
      }
    } else if (frequency === "Quarterly") {
      if (periodType === "recently-completed") {
        return (
          <>
            On{" "}
            <span className="recurring-tasks__example-highlight">
              02-Jan-2026
            </span>
            , a task is created for{" "}
            <span className="recurring-tasks__example-highlight">
              FY 2025-26 | Oct-Dec
            </span>
            <br />
            On{" "}
            <span className="recurring-tasks__example-highlight">
              02-Apr-2026
            </span>
            , a task is created for{" "}
            <span className="recurring-tasks__example-highlight">
              FY 2025-26 | Jan-Mar
            </span>
            <br />
            On{" "}
            <span className="recurring-tasks__example-highlight">
              02-Jul-2026
            </span>
            , a task is created for{" "}
            <span className="recurring-tasks__example-highlight">
              FY 2026-27 | Apr-Jun
            </span>
            , and so on.
          </>
        );
      } else {
        return (
          <>
            On{" "}
            <span className="recurring-tasks__example-highlight">
              02-Jan-2026
            </span>
            , a task is created for{" "}
            <span className="recurring-tasks__example-highlight">
              FY 2025-26 | Jan-Mar
            </span>
            <br />
            On{" "}
            <span className="recurring-tasks__example-highlight">
              02-Apr-2026
            </span>
            , a task is created for{" "}
            <span className="recurring-tasks__example-highlight">
              FY 2026-27 | Apr-Jun
            </span>
            , and so on.
          </>
        );
      }
    }
    return null;
  };

  const clients = [
    "Acme Corporation",
    "TechStart Solutions",
    "Global Enterprises",
    "Innovative Systems",
    "Prime Industries",
    "NextGen Digital",
    "Apex Technologies",
    "Summit Consulting",
    "Horizon Ventures",
    "Elite Partners",
  ];

  const renderDateFields = () => {
    if (frequency === "Monthly") {
      return (
        <div className="recurring-tasks__date-fields">
          <div className="recurring-tasks__date-field">
            <label className="recurring-tasks__date-label">
              <Calendar className="recurring-tasks__date-icon" />
              Creation Date:
            </label>
            <div className="recurring-tasks__date-select-group">
              <select
                value={monthlyCreationDay}
                onChange={(e) => setMonthlyCreationDay(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--flex"
              >
                <option>2th</option>
                <option>1st</option>
                <option>3rd</option>
                <option>5th</option>
                <option>10th</option>
                <option>15th</option>
                <option>20th</option>
                <option>Last</option>
              </select>
              <select
                value={monthlyCreationPeriod}
                onChange={(e) => setMonthlyCreationPeriod(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--flex"
              >
                <option>Of Every Month</option>
              </select>
            </div>
          </div>

          <div className="recurring-tasks__date-field">
            <label className="recurring-tasks__date-label">
              <Calendar className="recurring-tasks__date-icon" />
              Due Date:
            </label>
            <div className="recurring-tasks__date-select-group">
              <select
                value={monthlyDueDay}
                onChange={(e) => setMonthlyDueDay(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--flex"
              >
                <option>11th</option>
                <option>1st</option>
                <option>5th</option>
                <option>10th</option>
                <option>15th</option>
                <option>20th</option>
                <option>Last</option>
              </select>
              <select
                value={monthlyDuePeriod}
                onChange={(e) => setMonthlyDuePeriod(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--flex"
              >
                <option>Of Every Month</option>
              </select>
            </div>
          </div>
        </div>
      );
    } else if (frequency === "Yearly") {
      return (
        <div className="recurring-tasks__date-fields">
          <div className="recurring-tasks__date-field">
            <label className="recurring-tasks__date-label">
              <Calendar className="recurring-tasks__date-icon" />
              Creation Date:
            </label>
            <div className="recurring-tasks__date-select-group">
              <select
                value={yearlyCreationDay}
                onChange={(e) => setYearlyCreationDay(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--flex"
              >
                <option>2 Dec</option>
                <option>1 Jan</option>
                <option>15 Mar</option>
                <option>30 Jun</option>
              </select>
              <select
                value={yearlyCreationPeriod}
                onChange={(e) => setYearlyCreationPeriod(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--flex"
              >
                <option>Of Every Calendar Year</option>
              </select>
            </div>
          </div>

          <div className="recurring-tasks__date-field">
            <label className="recurring-tasks__date-label">
              <Calendar className="recurring-tasks__date-icon" />
              Due Date:
            </label>
            <div className="recurring-tasks__date-select-group">
              <select
                value={yearlyDueDay}
                onChange={(e) => setYearlyDueDay(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--flex"
              >
                <option>1 Jan</option>
                <option>2 Dec</option>
                <option>15 Mar</option>
                <option>30 Jun</option>
              </select>
              <select
                value={yearlyDuePeriod}
                onChange={(e) => setYearlyDuePeriod(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--flex"
              >
                <option>Of Every Calendar Year</option>
              </select>
            </div>
          </div>
        </div>
      );
    } else if (frequency === "Half-yearly") {
      return (
        <div className="recurring-tasks__date-fields">
          <div className="recurring-tasks__date-field">
            <label className="recurring-tasks__date-label">
              <Calendar className="recurring-tasks__date-icon" />
              Creation Date:
            </label>
            <div className="recurring-tasks__date-select-group recurring-tasks__date-select-group--wrap">
              <select
                value={halfYearlyCreationDay}
                onChange={(e) => setHalfYearlyCreationDay(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--small"
              >
                <option>2nd</option>
                <option>1st</option>
                <option>5th</option>
                <option>10th</option>
                <option>15th</option>
              </select>
              <span className="recurring-tasks__date-separator">OF</span>
              <select
                value={halfYearlyCreationMonth1}
                onChange={(e) => setHalfYearlyCreationMonth1(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--medium"
              >
                <option>April</option>
                <option>January</option>
                <option>February</option>
                <option>March</option>
              </select>
              <span className="recurring-tasks__date-separator">&</span>
              <select
                value={halfYearlyCreationMonth2}
                onChange={(e) => setHalfYearlyCreationMonth2(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--medium"
              >
                <option>Oct</option>
                <option>July</option>
                <option>August</option>
                <option>September</option>
              </select>
              <span className="recurring-tasks__date-text">Month</span>
            </div>
          </div>

          <div className="recurring-tasks__date-field">
            <label className="recurring-tasks__date-label">
              <Calendar className="recurring-tasks__date-icon" />
              Due Date:
            </label>
            <div className="recurring-tasks__date-select-group recurring-tasks__date-select-group--wrap">
              <select
                value={halfYearlyDueDay}
                onChange={(e) => setHalfYearlyDueDay(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--small"
              >
                <option>11th</option>
                <option>1st</option>
                <option>5th</option>
                <option>10th</option>
                <option>15th</option>
              </select>
              <span className="recurring-tasks__date-separator">OF</span>
              <select
                value={halfYearlyDueMonth1}
                onChange={(e) => setHalfYearlyDueMonth1(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--medium"
              >
                <option>May</option>
                <option>January</option>
                <option>February</option>
                <option>March</option>
              </select>
              <span className="recurring-tasks__date-separator">&</span>
              <select
                value={halfYearlyDueMonth2}
                onChange={(e) => setHalfYearlyDueMonth2(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--medium"
              >
                <option>Nov</option>
                <option>July</option>
                <option>August</option>
                <option>September</option>
              </select>
              <span className="recurring-tasks__date-text">Month</span>
            </div>
          </div>
        </div>
      );
    } else if (frequency === "Quarterly") {
      return (
        <div className="recurring-tasks__date-fields">
          <div className="recurring-tasks__date-field">
            <label className="recurring-tasks__date-label">
              <Calendar className="recurring-tasks__date-icon" />
              Creation Date:
            </label>
            <div className="recurring-tasks__date-select-group recurring-tasks__date-select-group--wrap">
              <select
                value={quarterlyCreationDay}
                onChange={(e) => setQuarterlyCreationDay(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--small"
              >
                <option>2th</option>
                <option>1st</option>
                <option>5th</option>
                <option>10th</option>
              </select>
              <span className="recurring-tasks__date-separator">Of</span>
              <select
                value={quarterlyCreationPosition}
                onChange={(e) => setQuarterlyCreationPosition(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--medium"
              >
                <option>Second</option>
                <option>First</option>
                <option>Third</option>
              </select>
              <select
                value={quarterlyCreationType}
                onChange={(e) => setQuarterlyCreationType(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--large"
              >
                <option>Month of every quarter</option>
                <option>Month of even quarter</option>
                <option>Month of odd quarter</option>
              </select>
            </div>
          </div>

          <div className="recurring-tasks__date-field">
            <label className="recurring-tasks__date-label">
              <Calendar className="recurring-tasks__date-icon" />
              Due Date:
            </label>
            <div className="recurring-tasks__date-select-group recurring-tasks__date-select-group--wrap">
              <select
                value={quarterlyDueDay}
                onChange={(e) => setQuarterlyDueDay(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--small"
              >
                <option>11th</option>
                <option>1st</option>
                <option>5th</option>
                <option>10th</option>
              </select>
              <span className="recurring-tasks__date-separator">Of</span>
              <select
                value={quarterlyDuePosition}
                onChange={(e) => setQuarterlyDuePosition(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--medium"
              >
                <option>Second</option>
                <option>First</option>
                <option>Third</option>
              </select>
              <select
                value={quarterlyDueType}
                onChange={(e) => setQuarterlyDueType(e.target.value)}
                className="recurring-tasks__select recurring-tasks__select--large"
              >
                <option>Month of every quarter</option>
                <option>Month of even quarter</option>
                <option>Month of odd quarter</option>
              </select>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <section className="recurring-tasks">
      {/* Background Elements */}
      <div className="recurring-tasks__background">
        <div className="recurring-tasks__blob recurring-tasks__blob--1"></div>
        <div className="recurring-tasks__blob recurring-tasks__blob--2"></div>
      </div>

      <div className="recurring-tasks__container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="recurring-tasks__header"
        >
          <div className="recurring-tasks__badge">
            <Repeat className="recurring-tasks__badge-icon" />
            <span className="recurring-tasks__badge-text">Recurring Tasks</span>
          </div>

          <h2 className="recurring-tasks__title">Automate Repetitive Work</h2>
          <p className="recurring-tasks__subtitle">
            Set up tasks that repeat yearly, half-yearly, quarterly, or monthly
            with intelligent period-based creation.
          </p>
        </motion.div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="recurring-tasks__content-wrapper"
        >
          <div className="recurring-tasks__card">
            {/* Recurring Toggle Header */}
            <div className="recurring-tasks__card-header">
              <div className="recurring-tasks__card-title">
                <Repeat className="recurring-tasks__card-icon" />
                <h3 className="recurring-tasks__card-heading">Recurring</h3>
              </div>
              <button
                onClick={() => setRecurringEnabled(!recurringEnabled)}
                className={`recurring-tasks__toggle ${recurringEnabled ? "recurring-tasks__toggle--active" : ""}`}
              >
                <motion.div
                  className="recurring-tasks__toggle-handle"
                  animate={{ x: recurringEnabled ? 28 : 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              </button>
            </div>

            <AnimatePresence>
              {recurringEnabled && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="recurring-tasks__content"
                >
                  {/* Frequency Selector */}
                  <div className="recurring-tasks__field">
                    <label className="recurring-tasks__label">Frequency:</label>
                    <select
                      value={frequency}
                      onChange={(e) => setFrequency(e.target.value)}
                      className="recurring-tasks__select"
                    >
                      <option>Monthly</option>
                      <option>Quarterly</option>
                      <option>Half-yearly</option>
                      <option>Yearly</option>
                    </select>
                  </div>

                  {/* Dynamic Date Fields */}
                  {renderDateFields()}

                  {/* Period Selection */}
                  <div className="recurring-tasks__section">
                    <h4 className="recurring-tasks__section-title">
                      Which period should recurring tasks be created for?
                    </h4>
                    <p className="recurring-tasks__section-description">
                      Choose whether the next task should belong to the last
                      completed month or the current month when it's created.
                    </p>

                    <div className="recurring-tasks__period-options">
                      {/* Recently Completed Option */}
                      <div
                        onClick={() => setPeriodType("recently-completed")}
                        className={`recurring-tasks__period-option ${periodType === "recently-completed" ? "recurring-tasks__period-option--active" : ""}`}
                      >
                        <div className="recurring-tasks__period-option-content">
                          <div
                            className={`recurring-tasks__period-radio ${periodType === "recently-completed" ? "recurring-tasks__period-radio--active" : ""}`}
                          >
                            {periodType === "recently-completed" && (
                              <div className="recurring-tasks__period-radio-dot" />
                            )}
                          </div>
                          <div className="recurring-tasks__period-option-text">
                            <h5 className="recurring-tasks__period-option-title">
                              Create Tasks for the{" "}
                              <span className="recurring-tasks__period-option-highlight">
                                Recently Completed Period
                              </span>
                            </h5>
                            <p className="recurring-tasks__period-option-description">
                              Use this if your tasks are always created for the
                              last completed month or the month before it
                              (typically works well for January belonging to
                              December period).
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Currently Running Option */}
                      <div
                        onClick={() => setPeriodType("currently-running")}
                        className={`recurring-tasks__period-option ${periodType === "currently-running" ? "recurring-tasks__period-option--active" : ""}`}
                      >
                        <div className="recurring-tasks__period-option-content">
                          <div
                            className={`recurring-tasks__period-radio ${periodType === "currently-running" ? "recurring-tasks__period-radio--active" : ""}`}
                          >
                            {periodType === "currently-running" && (
                              <div className="recurring-tasks__period-radio-dot" />
                            )}
                          </div>
                          <div className="recurring-tasks__period-option-text">
                            <h5 className="recurring-tasks__period-option-title">
                              Create Tasks for the{" "}
                              <span className="recurring-tasks__period-option-highlight">
                                Currently Running Period
                              </span>
                            </h5>
                            <p className="recurring-tasks__period-option-description">
                              Use this when tasks belong to the same month as
                              when it's created (e.g., salary processing, etc.
                              for the current month).
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Examples Section */}
                  <div className="recurring-tasks__section">
                    <h4 className="recurring-tasks__section-title">
                      Example (
                      {periodType === "recently-completed"
                        ? "Recently Completed Period"
                        : "Currently Running Period"}
                      ):
                    </h4>

                    {/* Example Table */}
                    <div className="recurring-tasks__table-wrapper">
                      <table className="recurring-tasks__table">
                        <thead className="recurring-tasks__table-head">
                          <tr>
                            <th className="recurring-tasks__table-header">
                              Task = FY & Period
                            </th>
                            <th className="recurring-tasks__table-header">
                              Creation Date
                            </th>
                            <th className="recurring-tasks__table-header recurring-tasks__table-header--due">
                              Due Date
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {getExampleData().map((row, index) => (
                            <tr
                              key={index}
                              className="recurring-tasks__table-row"
                            >
                              <td className="recurring-tasks__table-cell recurring-tasks__table-cell--task">
                                {row.task}
                              </td>
                              <td className="recurring-tasks__table-cell recurring-tasks__table-cell--creation">
                                {row.creationDate}
                              </td>
                              <td className="recurring-tasks__table-cell recurring-tasks__table-cell--due">
                                {row.dueDate}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Example Note */}
                    <div className="recurring-tasks__note recurring-tasks__note--yellow">
                      <p className="recurring-tasks__note-text">
                        <span className="recurring-tasks__note-label">
                          Example:
                        </span>
                        <br />
                        {getExampleText()}
                      </p>
                    </div>
                  </div>

                  {/* Auto-Assign Section */}
                  <div className="recurring-tasks__section">
                    <div className="recurring-tasks__field recurring-tasks__field--row">
                      <label className="recurring-tasks__label">
                        Assign Auto Task to the Last Assignee of this Task +
                        Client
                      </label>
                      <button
                        onClick={() => setAutoAssignEnabled(!autoAssignEnabled)}
                        className={`recurring-tasks__toggle ${autoAssignEnabled ? "recurring-tasks__toggle--active" : ""}`}
                      >
                        <motion.div
                          className="recurring-tasks__toggle-handle"
                          // animate={{ x: autoAssignEnabled ? 13 : 0 }}
                          animate={{ x: autoAssignEnabled ? offset : 0 }}
                          transition={{
                            type: "spring",
                            stiffness: 500,
                            damping: 30,
                          }}
                        />
                      </button>
                    </div>

                    <div className="recurring-tasks__field">
                      <label className="recurring-tasks__label">
                        Assign Auto Task...
                      </label>
                      <select
                        value={autoAssignTo}
                        onChange={(e) => setAutoAssignTo(e.target.value)}
                        disabled={!autoAssignEnabled}
                        className={`recurring-tasks__select ${!autoAssignEnabled ? "recurring-tasks__select--disabled" : ""}`}
                      >
                        <option>Select</option>
                        <option>John Doe</option>
                        <option>Jane Smith</option>
                        <option>Mike Johnson</option>
                      </select>
                    </div>

                    {/* Note */}
                    <div className="recurring-tasks__note recurring-tasks__note--blue">
                      <h5 className="recurring-tasks__note-title">Note:</h5>
                      <p className="recurring-tasks__note-text">
                        All Tasks will be scheduled for future dates
                        exclusively.
                        <br />
                        This means that tasks will be created for tomorrow or
                        subsequent days.
                        <br />
                        Tasks for today's date or past days cannot be generated.
                      </p>
                    </div>
                  </div>

                  {/* Recurring (Clients) Section */}
                  <div className="recurring-tasks__section">
                    <div className="recurring-tasks__clients-header">
                      <div className="recurring-tasks__clients-title-wrapper">
                        <Repeat className="recurring-tasks__clients-icon" />
                        <h4 className="recurring-tasks__clients-title">
                          Recurring (Clients)
                        </h4>
                        <span className="recurring-tasks__clients-badge">
                          Clients (10)
                        </span>
                      </div>
                      <button className="recurring-tasks__clients-link">
                        <ExternalLink className="recurring-tasks__clients-link-icon" />
                      </button>
                    </div>

                    {/* Clients List */}
                    <div className="recurring-tasks__clients-grid">
                      {clients.map((client, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.01 }}
                          className="recurring-tasks__client-item"
                        >
                          {client}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default RecurringTasksEnhanced;
